/**
 * The hardware order builder on a case (procurement.md §4, round 11): pick a Books vendor, search the Books item
 * catalog (or propose a new product through the create_product card), set quantities with each line's price source
 * visible (Books price / cost +75% / override), choose the ship-to and needed-by, then review the order card. The
 * order card is the request gate: confirming it creates the Books PO and submits it for Books' own approval.
 * Owner: web-procurement.
 */
import { Button, Chip, Dialog, EmptyState, ErrorBanner, Input, Picker, SkeletonRows, Textarea } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PackagePlus, Plus, Search, Trash2 } from 'lucide-react'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import {
  type Card,
  type OrderProposeIn,
  type ProductOut,
  type ProductProposeIn,
  procurementApi,
  procurementKeys,
  type ShipTo,
  type VendorOut,
} from './api'
import { ProcurementConfirmCard } from './card'
import { linePrice, money, toCents } from './pricing'

export interface DraftLine {
  product: ProductOut
  qty: number
  override: string
  overriding: boolean
}

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = React.useState(value)
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** The API dedupes product/order drafts on this UUID, including a retry after an uncertain response. */
export function newClientRequestId(): string {
  return globalThis.crypto.randomUUID()
}

/** Case-view adapters use the literal `unknown` when no site address was resolved; never ship to that sentinel. */
export function knownSiteAddress(value: string | null | undefined): string {
  const address = value?.trim() ?? ''
  return address.toLowerCase() === 'unknown' ? '' : address
}

function VendorPicker({ value, onChange }: { value: VendorOut | null; onChange: (v: VendorOut | null) => void }) {
  const vendors = React.useRef(new Map<string, VendorOut>())
  const health = useQuery({ queryKey: procurementKeys.catalogHealth(), queryFn: procurementApi.catalogHealth, staleTime: 60_000 })
  if (value) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">{value.name}</span>
        <span className="text-xs text-muted-foreground">Books vendor {value.books_contact_id}</span>
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      <Picker
        ariaLabel="Search vendors"
        value=""
        options={[]}
        placeholder="Search Books vendors (2+ letters)"
        emptyMessage="Type at least 2 characters"
        errorMessage="Vendor lookup unreachable"
        loadOptions={async (query, signal) => {
          const term = query.trim()
          if (term.length < 2) return []
          const rows = await procurementApi.vendors(term)
          if (signal.aborted) return []
          vendors.current = new Map(rows.map((vendor) => [vendor.id, vendor]))
          return rows.map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
            description: vendor.is_contractor_firm ? 'Also a contractor firm' : `Books vendor ${vendor.books_contact_id}`,
          }))
        }}
        onValueChange={(id) => onChange(vendors.current.get(id) ?? null)}
      />
      {health.isError ? (
        <ErrorBanner
          tone="warning"
          title="Books vendor catalog health is unreachable"
          detail="Search results may be incomplete; no absence was inferred."
        />
      ) : health.data && !health.data.vendors.ok ? (
        <ErrorBanner
          tone="warning"
          title="Books vendor catalog is stale"
          detail={`The last vendor refresh failed (${health.data.vendors.error ?? 'unreachable'}). Search results may be incomplete.`}
        />
      ) : null}
    </div>
  )
}

function CreateProduct({ caseId, initialName, onDone }: { caseId: string; initialName: string; onDone: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = React.useState<ProductProposeIn>(() => ({
    client_request_id: newClientRequestId(),
    case_id: caseId,
    name: initialName,
    is_serialized: true,
  }))
  const [card, setCard] = React.useState<Card | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [proposing, setProposing] = React.useState(false)
  const proposeInFlight = React.useRef(false)
  const set = (k: keyof ProductProposeIn) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setForm((f) => ({ ...f, [k]: k === 'name' ? value : value || null }))
  }
  const badMoney = (v: string | null | undefined) => Boolean(v) && toCents(v) === null

  if (card) {
    return (
      <ProcurementConfirmCard
        card={card}
        onChange={(c) => {
          setCard(c)
          if (c === null) setForm((f) => ({ ...f, client_request_id: newClientRequestId() }))
        }}
        onSettled={async () => {
          await qc.invalidateQueries({ queryKey: ['procurement', 'products'] })
          onDone()
        }}
      >
        <p>
          New Books item <span className="font-medium">{form.name}</span>
          {form.sku ? ` · SKU ${form.sku}` : ''}
          {form.model ? ` · model ${form.model}` : ''}
          {form.is_serialized ? ' · serial-tracked' : ''}
        </p>
      </ProcurementConfirmCard>
    )
  }
  return (
    <fieldset className="space-y-2 rounded-md border border-dashed border-border-strong p-3" aria-label="Create product">
      <legend className="px-1 text-sm font-medium">Create a product in Books</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input aria-label="Product name" placeholder="Name" value={form.name} onChange={set('name')} />
        <Input aria-label="SKU" placeholder="SKU" value={form.sku ?? ''} onChange={set('sku')} />
        <Input aria-label="Model" placeholder="Model" value={form.model ?? ''} onChange={set('model')} />
        <Input
          aria-label="Vendor part number"
          placeholder="Vendor part number"
          value={form.vendor_part_number ?? ''}
          onChange={set('vendor_part_number')}
        />
        <Input aria-label="Cost" placeholder="Cost (e.g. 120.00)" inputMode="decimal" value={form.cost ?? ''} onChange={set('cost')} />
        <Input
          aria-label="Sell price"
          placeholder="Sell price (blank: cost + markup)"
          inputMode="decimal"
          value={form.sell_price ?? ''}
          onChange={set('sell_price')}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.is_serialized}
          onChange={(e) => {
            const checked = e.currentTarget.checked
            setForm((f) => ({ ...f, is_serialized: checked }))
          }}
        />
        Serial-tracked (record a serial per unit on receive)
      </label>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={proposing || !form.name.trim() || badMoney(form.cost) || badMoney(form.sell_price)}
          onClick={async () => {
            if (proposeInFlight.current) return
            proposeInFlight.current = true
            setProposing(true)
            setError(null)
            try {
              setCard(await procurementApi.proposeProduct({ ...form, name: form.name.trim() }))
            } catch (err) {
              setError(`Could not propose the product: ${describeError(err)}`)
            } finally {
              proposeInFlight.current = false
              setProposing(false)
            }
          }}
        >
          Review create_product card
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </fieldset>
  )
}

function ProductSearch({ caseId, onAdd }: { caseId: string; onAdd: (p: ProductOut, markupPct: number | null) => void }) {
  const [q, setQ] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  const term = useDebounced(q.trim())
  const products = useQuery({
    queryKey: procurementKeys.products(term),
    queryFn: () => procurementApi.products(term),
    enabled: term.length >= 2,
    staleTime: 60_000,
  })
  const health = useQuery({ queryKey: procurementKeys.catalogHealth(), queryFn: procurementApi.catalogHealth, staleTime: 60_000 })
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute top-2 left-2 size-4 text-muted-foreground" aria-hidden />
          <Input
            aria-label="Search products"
            placeholder="Search Books items by name, SKU or part number"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            className="pl-8"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)} disabled={creating}>
          <PackagePlus /> Create product
        </Button>
      </div>
      {creating ? <CreateProduct caseId={caseId} initialName={q.trim()} onDone={() => setCreating(false)} /> : null}
      {products.isFetching && !products.data ? <SkeletonRows count={2} /> : null}
      {products.isError ? (
        <ErrorBanner
          tone="warning"
          title="Product catalog unreachable"
          detail={describeError(products.error)}
          onRetry={() => void products.refetch()}
        />
      ) : null}
      {health.isError ? (
        <ErrorBanner
          tone="warning"
          title="Books product catalog health is unreachable"
          detail="Search results may be incomplete; no absence was inferred."
        />
      ) : health.data && !health.data.products.ok ? (
        <ErrorBanner
          tone="warning"
          title="Books product catalog is stale"
          detail={`The last product refresh failed (${health.data.products.error ?? 'unreachable'}). Search results may be incomplete.`}
        />
      ) : null}
      {products.isSuccess && products.data.items.length === 0 && health.data?.products.ok === true ? (
        <p className="text-sm text-muted-foreground">No Books item matched "{term}". Create it if it is new.</p>
      ) : null}
      {products.data?.items.length ? (
        <ul className="max-h-48 overflow-y-auto rounded-md border border-border">
          {products.data.items.map((p) => {
            const price = linePrice(p, products.data.markup_pct)
            return (
              <li key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm">
                <span className="min-w-0 flex-1">
                  {p.name}
                  <span className="block text-xs text-muted-foreground">
                    {[p.sku, p.model, p.vendor_part_number].filter(Boolean).join(' · ') || 'no SKU'} · {price.label}
                  </span>
                </span>
                <Button size="sm" variant="ghost" aria-label={`Add ${p.name}`} onClick={() => onAdd(p, products.data.markup_pct ?? null)}>
                  <Plus /> Add
                </Button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export function buildOrderIn(
  caseId: string,
  vendor: VendorOut,
  shipTo: ShipTo,
  neededBy: string,
  lines: DraftLine[],
  markupPct: number | null,
  clientRequestId: string,
  manualPo: { on: boolean; number: string } = { on: false, number: '' },
): OrderProposeIn {
  return {
    po_mode: manualPo.on ? 'manual' : 'books',
    manual_po_number: manualPo.on ? manualPo.number.trim() || null : null,
    client_request_id: clientRequestId,
    case_id: caseId,
    vendor_id: vendor.id,
    ship_to: shipTo.kind === 'office' ? { kind: 'office' } : { kind: 'customer_site', address: shipTo.address?.trim() || null },
    needed_by: neededBy || null,
    lines: lines.map((l) => {
      const p = linePrice(l.product, markupPct, l.overriding ? l.override : null)
      return { product_id: l.product.id, qty: l.qty, sell_source: p.source, unit_sell: p.source === 'override' ? p.unitSell : null }
    }),
  }
}

export function OrderBuilder({
  caseId,
  open,
  onOpenChange,
  siteAddress,
}: {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The case's site, offered as the drop-ship address. */
  siteAddress?: string | null
}) {
  const qc = useQueryClient()
  const [vendor, setVendor] = React.useState<VendorOut | null>(null)
  const [lines, setLines] = React.useState<DraftLine[]>([])
  const [markupPct, setMarkupPct] = React.useState<number | null>(null)
  const [shipTo, setShipTo] = React.useState<ShipTo>({ kind: 'office' })
  const [neededBy, setNeededBy] = React.useState('')
  const [manualPo, setManualPo] = React.useState({ on: false, number: '' })
  const [card, setCard] = React.useState<Card | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [proposing, setProposing] = React.useState(false)
  const proposeInFlight = React.useRef(false)
  const clientRequestId = React.useRef(newClientRequestId())

  const add = (p: ProductOut, pct: number | null) => {
    setMarkupPct(pct)
    setLines((ls) => (ls.some((l) => l.product.id === p.id) ? ls : [...ls, { product: p, qty: 1, override: '', overriding: false }]))
  }
  const update = (i: number, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))

  const lineIssues = lines.some((l) => !Number.isInteger(l.qty) || l.qty < 1 || (l.overriding && toCents(l.override) === null))
  const shipIssue = shipTo.kind === 'customer_site' && !shipTo.address?.trim()
  const ready = Boolean(vendor) && lines.length > 0 && !lineIssues && !shipIssue

  const total = lines.reduce<number | null>((sum, l) => {
    const p = linePrice(l.product, markupPct, l.overriding ? l.override : null)
    const c = toCents(p.unitSell)
    return sum === null || c === null ? null : sum + c * l.qty
  }, 0)

  const reset = () => {
    setVendor(null)
    setLines([])
    setShipTo({ kind: 'office' })
    setNeededBy('')
    setManualPo({ on: false, number: '' })
    setCard(null)
    setError(null)
    setProposing(false)
    proposeInFlight.current = false
    clientRequestId.current = newClientRequestId()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && card?.state !== 'proposed') reset()
        onOpenChange(o)
      }}
      title="Order hardware"
      description="Confirming the order card creates a Books purchase order and submits it for approval in Books, or records it as 'PO to be raised in Books' when you raise the PO by hand."
    >
      {card ? (
        <div className="space-y-3">
          <ProcurementConfirmCard
            card={card}
            onChange={(next) => {
              setCard(next)
              if (next === null) clientRequestId.current = newClientRequestId()
            }}
            onSettled={() => qc.invalidateQueries({ queryKey: procurementKeys.case(caseId) })}
          >
            <p>
              {lines.length} line{lines.length === 1 ? '' : 's'} from <span className="font-medium">{vendor?.name}</span> ·{' '}
              {shipTo.kind === 'office' ? 'ship to our office' : `drop-ship to ${shipTo.address}`}
              {neededBy ? ` · needed by ${neededBy}` : ''}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {manualPo.on
                ? `PO to be raised in Books by hand${manualPo.number.trim() ? ` (PO ${manualPo.number.trim()})` : ''}: Patchbay makes no Books call.`
                : 'Books\' own approval rules decide; Patchbay never approves the PO itself. If Books refuses the PO, the order is recorded as "PO to be raised in Books".'}
            </p>
          </ProcurementConfirmCard>
          {card.state !== 'proposed' ? (
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  reset()
                  onOpenChange(false)
                }}
              >
                Close
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold">Vendor</h3>
            <VendorPicker value={vendor} onChange={setVendor} />
          </section>

          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold">Products</h3>
            <ProductSearch caseId={caseId} onAdd={add} />
            {lines.length === 0 ? (
              <EmptyState title="No lines yet." detail="Search the Books catalog and add each item." className="py-4" />
            ) : (
              <ul className="space-y-2" aria-label="Order lines">
                {lines.map((l, i) => {
                  const price = linePrice(l.product, markupPct, l.overriding ? l.override : null)
                  return (
                    <li key={l.product.id} className="rounded-md border border-border p-2.5 text-sm" data-line={l.product.id}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1 font-medium">{l.product.name}</span>
                        <label className="flex items-center gap-1 text-xs">
                          Qty
                          <Input
                            aria-label={`Quantity of ${l.product.name}`}
                            inputMode="numeric"
                            className="h-7 w-16"
                            value={String(l.qty)}
                            onChange={(e) => update(i, { qty: Number(e.currentTarget.value) })}
                          />
                        </label>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Remove ${l.product.name}`}
                          onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <span className="tabular">
                          {price.unitSell === null ? 'Price set at confirm' : `${money(price.unitSell)} each`}
                        </span>
                        <Chip tone={price.source === 'override' ? 'warning' : 'outline'} data-sell-source={price.source}>
                          {price.label}
                        </Chip>
                        {l.product.cost !== undefined && l.product.cost !== null ? (
                          <span className="text-xs text-muted-foreground">cost {money(l.product.cost)}</span>
                        ) : null}
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={l.overriding}
                            onChange={(e) => update(i, { overriding: e.currentTarget.checked })}
                          />
                          Override price
                        </label>
                        {l.overriding ? (
                          <Input
                            aria-label={`Override sell price of ${l.product.name}`}
                            inputMode="decimal"
                            placeholder="0.00"
                            className="h-7 w-28"
                            value={l.override}
                            onChange={(e) => update(i, { override: e.currentTarget.value })}
                          />
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
            {lines.length ? (
              <p className="text-right text-sm font-medium">
                Sell total{' '}
                {total === null ? 'is set when the card is confirmed (a price is not visible to you)' : money((total / 100).toFixed(2))}
              </p>
            ) : null}
          </section>

          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold">Ship to</h3>
            <div className="flex flex-wrap gap-4 text-sm" role="radiogroup" aria-label="Ship to">
              <label className="flex items-center gap-1.5">
                <input type="radio" name="ship-to" checked={shipTo.kind === 'office'} onChange={() => setShipTo({ kind: 'office' })} />
                Our office (receive, record serials, stage)
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="ship-to"
                  checked={shipTo.kind === 'customer_site'}
                  onChange={() => setShipTo({ kind: 'customer_site', address: knownSiteAddress(shipTo.address ?? siteAddress) })}
                />
                Drop-ship to the customer site (serials recorded at install)
              </label>
            </div>
            {shipTo.kind === 'customer_site' ? (
              <Textarea
                aria-label="Site address"
                placeholder="Site address"
                value={shipTo.address ?? ''}
                onChange={(e) => setShipTo({ kind: 'customer_site', address: e.currentTarget.value })}
              />
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              Needed by
              <Input
                type="date"
                aria-label="Needed by"
                className="h-8 w-44"
                value={neededBy}
                onChange={(e) => setNeededBy(e.currentTarget.value)}
              />
            </label>
          </section>

          <section className="space-y-1.5" aria-label="Purchase order">
            <h3 className="text-sm font-semibold">Purchase order</h3>
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={manualPo.on}
                onChange={(e) => {
                  const on = e.currentTarget.checked
                  setManualPo((m) => ({ ...m, on }))
                }}
              />
              PO to be raised in Books by hand (no Books call; use while the Books token cannot create POs)
            </label>
            {manualPo.on ? (
              <Input
                aria-label="Manual PO number"
                placeholder="PO number from Books (optional now, enter it later on the order)"
                className="h-8"
                value={manualPo.number}
                onChange={(e) => {
                  const number = e.currentTarget.value
                  setManualPo((m) => ({ ...m, number }))
                }}
              />
            ) : null}
          </section>

          {error ? <ErrorBanner title="Order card not created" detail={error} /> : null}
          <div className="flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!ready || proposing}
              onClick={async () => {
                if (!vendor || proposeInFlight.current) return
                proposeInFlight.current = true
                setProposing(true)
                setError(null)
                try {
                  setCard(
                    await procurementApi.proposeOrder(
                      buildOrderIn(caseId, vendor, shipTo, neededBy, lines, markupPct, clientRequestId.current, manualPo),
                    ),
                  )
                } catch (err) {
                  setError(describeError(err))
                } finally {
                  proposeInFlight.current = false
                  setProposing(false)
                }
              }}
            >
              Review order card
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}
